import { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { Calendar, Tag, ArrowRight, Filter, Search, X } from 'lucide-react'
import type { ChangelogEntry } from '../types/changelog'
import { useTenant } from '../contexts/TenantContext'

const ChangelogPage = () => {
  const navigate = useNavigate()
  const { currentTenant } = useTenant()
  const [entries, setEntries] = useState<ChangelogEntry[]>([])
  const [loading, setLoading] = useState(true)
  const [selectedCategory, setSelectedCategory] = useState<string>('all')
  const [selectedTag, setSelectedTag] = useState<string>('')
  const [searchTerm, setSearchTerm] = useState('')
  const [showFilters, setShowFilters] = useState(false)

  // Track fetch call IDs to prevent stale state updates from concurrent fetches
  const fetchCallIdRef = useRef(0)

  // Extract unique tags from entries
  const allTags = Array.from(
    new Set(entries.flatMap(entry => entry.tags))
  ).sort()

  useEffect(() => {
    const controller = new AbortController()

    fetchEntries(controller.signal)

    return () => {
      controller.abort()
    }
  }, [currentTenant])

  const fetchEntries = async (signal?: AbortSignal) => {
    // Increment call ID to track this specific fetch request
    const currentCallId = ++fetchCallIdRef.current

    try {
      setLoading(true)
      const response = await fetch('/api/changelog', {
        signal
      })

      if (response.ok) {
        const data = await response.json()
        // Only update state if this is still the most recent call (prevents stale state)
        if (!signal?.aborted && currentCallId === fetchCallIdRef.current) {
          setEntries(data)
        }
      }
    } catch (error) {
      // Don't show error if request was aborted
      if (error instanceof Error && error.name === 'AbortError') {
        console.log('Changelog fetch was aborted')
        return
      }
      console.error('Error fetching changelog:', error)
    } finally {
      // Only update loading state if this is still the most recent call
      if (!signal?.aborted && currentCallId === fetchCallIdRef.current) {
        setLoading(false)
      }
    }
  }

  // Filter entries
  const filteredEntries = entries.filter(entry => {
    // Category filter
    if (selectedCategory !== 'all' && entry.category !== selectedCategory) {
      return false
    }

    // Tag filter
    if (selectedTag && !entry.tags.includes(selectedTag)) {
      return false
    }

    // Search filter
    if (searchTerm) {
      const search = searchTerm.toLowerCase()
      return (
        entry.title.toLowerCase().includes(search) ||
        entry.description.toLowerCase().includes(search) ||
        entry.content.toLowerCase().includes(search)
      )
    }

    return true
  })

  const getCategoryColor = (category: string) => {
    const colors: Record<string, string> = {
      feature: 'bg-blue-100 text-blue-800 border-blue-200',
      improvement: 'bg-green-100 text-green-800 border-green-200',
      fix: 'bg-yellow-100 text-yellow-800 border-yellow-200',
      security: 'bg-red-100 text-red-800 border-red-200',
      announcement: 'bg-purple-100 text-purple-800 border-purple-200'
    }
    return colors[category] || 'bg-gray-100 text-gray-800 border-gray-200'
  }

  const getCategoryLabel = (category: string) => {
    return category.charAt(0).toUpperCase() + category.slice(1)
  }

  const formatDate = (timestamp?: number) => {
    if (!timestamp) return ''
    return new Date(timestamp).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    })
  }

  const truncateContent = (content: string, maxLength: number = 200) => {
    const plainText = content.replace(/[#*_`]/g, '').trim()
    if (plainText.length <= maxLength) return plainText
    return plainText.substring(0, maxLength) + '...'
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-gradient-to-br from-primary-600 to-primary-800 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <h1 className="text-4xl font-bold mb-4">Product Changelog</h1>
          <p className="text-xl text-primary-100">
            Stay up to date with our latest improvements and features
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10 shadow-sm">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            {/* Search */}
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search changelog..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-10 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm('')}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>

            {/* Filter Toggle (Mobile) */}
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="sm:hidden flex items-center px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              <Filter className="h-4 w-4 mr-2" />
              Filters
            </button>

            {/* Category Filters (Desktop) */}
            <div className="hidden sm:flex items-center gap-2">
              {['all', 'feature', 'improvement', 'fix', 'security', 'announcement'].map((cat) => (
                <button
                  key={cat}
                  onClick={() => setSelectedCategory(cat)}
                  className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${
                    selectedCategory === cat
                      ? 'bg-primary-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {cat === 'all' ? 'All' : getCategoryLabel(cat)}
                </button>
              ))}
            </div>
          </div>

          {/* Mobile Filters */}
          {showFilters && (
            <div className="sm:hidden mt-4 pt-4 border-t border-gray-200">
              <div className="flex flex-wrap gap-2 mb-3">
                {['all', 'feature', 'improvement', 'fix', 'security', 'announcement'].map((cat) => (
                  <button
                    key={cat}
                    onClick={() => setSelectedCategory(cat)}
                    className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${
                      selectedCategory === cat
                        ? 'bg-primary-600 text-white'
                        : 'bg-gray-100 text-gray-700'
                    }`}
                  >
                    {cat === 'all' ? 'All' : getCategoryLabel(cat)}
                  </button>
                ))}
              </div>

              {allTags.length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Filter by tag:</label>
                  <select
                    value={selectedTag}
                    onChange={(e) => setSelectedTag(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  >
                    <option value="">All tags</option>
                    {allTags.map(tag => (
                      <option key={tag} value={tag}>{tag}</option>
                    ))}
                  </select>
                </div>
              )}
            </div>
          )}

          {/* Active Filters */}
          {(selectedCategory !== 'all' || selectedTag || searchTerm) && (
            <div className="flex items-center gap-2 mt-3 flex-wrap">
              <span className="text-sm text-gray-500">Active filters:</span>
              {selectedCategory !== 'all' && (
                <span className="inline-flex items-center px-2 py-1 bg-primary-100 text-primary-800 text-sm rounded">
                  {getCategoryLabel(selectedCategory)}
                  <button
                    onClick={() => setSelectedCategory('all')}
                    className="ml-1 hover:text-primary-900"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </span>
              )}
              {selectedTag && (
                <span className="inline-flex items-center px-2 py-1 bg-primary-100 text-primary-800 text-sm rounded">
                  {selectedTag}
                  <button
                    onClick={() => setSelectedTag('')}
                    className="ml-1 hover:text-primary-900"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </span>
              )}
              {searchTerm && (
                <span className="inline-flex items-center px-2 py-1 bg-primary-100 text-primary-800 text-sm rounded">
                  Search: "{searchTerm}"
                  <button
                    onClick={() => setSearchTerm('')}
                    className="ml-1 hover:text-primary-900"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </span>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading changelog...</p>
          </div>
        ) : filteredEntries.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-600">
              {entries.length === 0
                ? 'No changelog entries yet. Check back soon!'
                : 'No entries match your filters.'}
            </p>
          </div>
        ) : (
          <div className="space-y-6">
            {filteredEntries.map((entry) => (
              <article
                key={entry.id}
                className="bg-white rounded-lg border border-gray-200 hover:border-primary-300 hover:shadow-md transition-all cursor-pointer"
                onClick={() => navigate(`/changelog/${entry.slug}`)}
              >
                <div className="p-6">
                  {/* Date and Category */}
                  <div className="flex items-center gap-3 mb-3">
                    <div className="flex items-center text-sm text-gray-500">
                      <Calendar className="h-4 w-4 mr-1" />
                      {formatDate(entry.publishedAt || entry.createdAt)}
                    </div>
                    <span className={`px-2 py-1 text-xs font-medium rounded border ${getCategoryColor(entry.category)}`}>
                      {getCategoryLabel(entry.category)}
                    </span>
                    {entry.severity === 'critical' || entry.severity === 'high' ? (
                      <span className="px-2 py-1 text-xs font-medium rounded border bg-red-100 text-red-800 border-red-200">
                        {entry.severity.toUpperCase()}
                      </span>
                    ) : null}
                  </div>

                  {/* Title */}
                  <h2 className="text-2xl font-bold text-gray-900 mb-2 hover:text-primary-600">
                    {entry.title}
                  </h2>

                  {/* Description */}
                  {entry.description && (
                    <p className="text-gray-600 mb-4">
                      {entry.description}
                    </p>
                  )}

                  {/* Content Preview */}
                  <p className="text-gray-700 mb-4">
                    {truncateContent(entry.content)}
                  </p>

                  {/* Tags */}
                  {entry.tags.length > 0 && (
                    <div className="flex items-center gap-2 mb-4 flex-wrap">
                      <Tag className="h-4 w-4 text-gray-400" />
                      {entry.tags.map(tag => (
                        <span
                          key={tag}
                          className="px-2 py-1 text-xs bg-gray-100 text-gray-600 rounded hover:bg-gray-200"
                          onClick={(e) => {
                            e.stopPropagation()
                            setSelectedTag(tag)
                          }}
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  )}

                  {/* Read More */}
                  <div className="flex items-center text-primary-600 font-medium group">
                    Read more
                    <ArrowRight className="ml-2 h-4 w-4 transform group-hover:translate-x-1 transition-transform" />
                  </div>
                </div>
              </article>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

export default ChangelogPage
